'use client';

import { type ReactNode, type RefCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useCoursePageLinkClick } from '@/app/Components/CoursePeek/CourseLink';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import {
  EntityPreviewCard,
  EntityPreviewTrigger,
  type EntityPreviewAnchorProps,
} from '@/app/Components/EntityPreview';
import {
  buildAcademicBadgeStyle,
  buildAcademicSolidStyle,
  getCourseAcademicSwatch,
} from '@/app/lib/academicPalette';
import { formatBaseIdDisplay } from '@/app/lib/utils';

type AcademicCourseLabelProps = {
  courseName: string;
  baseId?: string | null;
  department?: string | null;
  classNumber?: string | number | null;
  credits?: string | number | null;
  className?: string;
  codeClassName?: string;
  showCode?: boolean;
  showSwatch?: boolean;
  compact?: boolean;
  preventParentClick?: boolean;
  variant?: 'badge' | 'solid';
  hoverPreview?: boolean;
  wrapTitle?: boolean;
  onActivate?: () => void;
  activationLabel?: string;
  trailingIcon?: ReactNode;
};

function buildCode(baseId?: string | null, department?: string | null, classNumber?: string | number | null) {
  if (baseId) return formatBaseIdDisplay(baseId);
  if (department && classNumber) return `${department}:${classNumber}`;
  return '';
}

export default function AcademicCourseLabel({
  courseName,
  baseId,
  department,
  classNumber,
  credits,
  className = '',
  codeClassName = '',
  showCode = true,
  showSwatch = true,
  compact = false,
  preventParentClick = false,
  variant = 'badge',
  hoverPreview = true,
  wrapTitle = false,
  onActivate,
  activationLabel,
  trailingIcon,
}: AcademicCourseLabelProps) {
  const router = useRouter();
  const openCoursePage = useCoursePageLinkClick();
  const { academicPalettePreferences, academicPalettePresets } = useUiPreferences();
  const code = buildCode(baseId, department, classNumber);
  const courseIdentity = getCourseAcademicSwatch(
    baseId ?? (department && classNumber ? `${department}:${classNumber}` : null),
    academicPalettePreferences.coursePaletteId,
    academicPalettePresets
  );
  const badgeStyle = courseIdentity.kind === 'accent'
    ? buildAcademicBadgeStyle(courseIdentity.swatch)
    : { backgroundColor: 'var(--ss-surface-subtle)', borderColor: 'var(--ss-border)', color: 'var(--ss-text)' };
  const solidStyle = courseIdentity.kind === 'accent'
    ? buildAcademicSolidStyle(courseIdentity.swatch)
    : { backgroundColor: 'var(--ss-surface-subtle)', color: 'var(--ss-text)' };
  const href = courseIdentity.normalizedBaseId
    ? `/courses/${encodeURIComponent(courseIdentity.normalizedBaseId)}`
    : null;

  const metaText = useMemo(() => {
    const segments = [
      code || null,
      credits ? `${credits} credits` : null,
    ].filter(Boolean);
    return segments.join(' • ');
  }, [code, credits]);

  const baseButtonClassName = compact
    ? 'group/course-label inline-flex max-w-full min-w-0 items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors'
    : 'group/course-label inline-flex max-w-full min-w-0 items-center gap-2 rounded-card border px-3 py-2 text-sm font-semibold transition-colors';

  const trigger = (previewProps: EntityPreviewAnchorProps) => (
      <button
        ref={previewProps.ref as RefCallback<HTMLButtonElement>}
        type="button"
        onClick={(event) => {
          if (preventParentClick || onActivate) {
            event.stopPropagation();
          }
          if (onActivate) {
            onActivate();
            return;
          }
          if (href && !openCoursePage(event, href)) router.push(href);
        }}
        aria-describedby={previewProps['aria-describedby']}
        onMouseEnter={previewProps.onMouseEnter}
        onMouseLeave={previewProps.onMouseLeave}
        onFocus={previewProps.onFocus}
        onBlur={previewProps.onBlur}
        className={`${baseButtonClassName} ${className}`}
        style={variant === 'solid' ? solidStyle : badgeStyle}
        aria-label={activationLabel}
        title={activationLabel ?? (href ? `Open ${courseName}` : courseName)}
      >
        {showSwatch && courseIdentity.kind === 'accent' ? <span className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full" style={solidStyle} aria-hidden="true" /> : null}
        <span className={`min-w-0 flex-1 text-left ${wrapTitle ? "whitespace-normal break-words" : "truncate"}`}>{courseName}</span>
        {showCode && code ? (
          <span className={`max-w-[42%] shrink-0 truncate text-[0.72em] opacity-80 ${codeClassName}`}>{code}</span>
        ) : null}
        {trailingIcon ? <span className="shrink-0">{trailingIcon}</span> : null}
      </button>
  );

  if (!hoverPreview) return trigger({
    ref: () => {},
    onMouseEnter: () => {},
    onMouseLeave: () => {},
    onFocus: () => {},
    onBlur: () => {},
  });

  return (
    <EntityPreviewTrigger
      label={`${courseName} course preview`}
      width={280}
      estimatedHeight={150}
      preview={(
        <EntityPreviewCard
          eyebrow="Course"
          title={courseName}
          metadata={metaText || code || 'Course details'}
          description={href || onActivate ? 'Open for complete course details and planning actions.' : 'A canonical course record is not available.'}
        >
          {courseIdentity.kind === 'accent' ? (
            <span className="flex items-center gap-2 text-xs font-semibold" style={{ color: courseIdentity.swatch.text }}>
              <span className="h-2.5 w-2.5 rounded-full" style={solidStyle} aria-hidden="true" />
              Academic identity
            </span>
          ) : null}
        </EntityPreviewCard>
      )}
    >
      {trigger}
    </EntityPreviewTrigger>
  );
}
